// CapaType Configuration - UNIFIED APPROACH
// Manages CAPA types (Corrective Action, Preventive Action, Both)
// Based on Prisma CapaType model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { tableConfig, uiConfig } from '../../../config/appConfig';

export const capaTypeConfig = {
  entityName: 'CapaType',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/capa-type',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // CapaType specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // CAPA types typically don't have lookup dependencies
      // but you could add severity levels, categories, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px', // Standard width for CAPA types
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'capaTypeService',
    methods: {
      getAll: 'getAllCapaTypes',
      getById: 'getCapaTypeById', 
      create: 'createCapaType',
      update: 'updateCapaType',
      delete: 'deleteCapaType',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'CAPA Type Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter CAPA type name (e.g., Corrective Action, Preventive Action)',
      table: { header: 'CAPA Type', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'CAPA type name must contain only letters, spaces, and common punctuation'
      }
    },
    { 
      name: 'code', 
      label: 'CAPA Type Code', 
      type: 'text', 
      required: true,
      placeholder: 'Enter unique code (e.g., CORRECTIVE, PREVENTIVE, BOTH)',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 50,
        pattern: /^[A-Z0-9_-]+$/,
        message: 'Code must contain only uppercase letters, numbers, underscores, and hyphens'
      },
      helperText: 'Unique identifier code (typically uppercase, e.g., CORRECTIVE)'
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      placeholder: 'Describe when this CAPA type should be used',
      table: { header: 'Description', width: '250px', sortable: false },
      filterable: true,
      validation: { 
        required: false, 
        maxLength: 500
      },
      rows: 3,
      helperText: 'Brief description of when to use this CAPA type'
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'CAPA type is active and available for use',
      description: 'Inactive CAPA types cannot be used for new CAPA records'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true,
        message: 'Display order must be a whole number between 1 and 999'
      },
      helperText: 'Controls the order in dropdown lists and displays',
      step: 1,
      decimals: 0,
      allowNegative: false
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color for visual categorization',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      filterable: false,
      validation: { 
        required: false,
        pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual categorization in lists and reports'
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'code', 'description', 'isActive'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'code', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['description', 'record_order']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false], // Active CAPA types first
    record_order: 'asc' // Ascending order by default
  },

  // Messages Configuration
  messages: {
    loading: 'Loading CAPA types...',
    noData: 'No CAPA types found. Click "Add CAPA Type" to create the first CAPA type.',
    loadError: 'Failed to load CAPA types',
    saveSuccess: 'CAPA type saved successfully!',
    saveError: 'Failed to save CAPA type',
    deleteSuccess: 'CAPA type deleted successfully!',
    deleteError: 'Failed to delete CAPA type',
    deleteConfirm: 'Are you sure you want to delete this CAPA type? This action cannot be undone and may affect CAPA records that reference this type.',
    activateSuccess: 'CAPA type activated successfully!',
    deactivateSuccess: 'CAPA type deactivated successfully!'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'CAPA Type Information',
        icon: '📋',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'code' },
          { name: 'isActive' },
          { name: 'record_order' },
          { name: 'color' }
        ]
      },
      {
        id: 'details',
        title: 'Additional Details',
        icon: '📝',
        columns: 1,
        fields: [
          { name: 'description' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('CapaType'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'CAPA Type',
    plural: 'CAPA Types',
    addButton: 'Add CAPA Type',
    editButton: 'Edit CAPA Type',
    deleteButton: 'Delete CAPA Type',
    
    // Custom labels for CAPA types
    pageTitle: 'CAPA Type Management',
    pageSubtitle: 'Manage CAPA types for corrective and preventive action classification',
    searchPlaceholder: 'Search CAPA types by name or code...',
    
    // Form labels
    basicInfoSection: 'CAPA Type Information',
    detailsSection: 'Additional Details',
    
    // Action labels
    activateCapaType: 'Activate CAPA Type',
    deactivateCapaType: 'Deactivate CAPA Type',
    reorderCapaTypes: 'Reorder CAPA Types'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'id',
    'createdAt',
    'updatedAt', 
    'createdById',
    'updatedById',
    // Remove all relation fields
    'capas'
  ],

  // Export filename for data exports
  exportFileName: 'capa-types',

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert record_order to integer or remove if empty
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        // Remove empty record_order field entirely
        delete transformed.record_order;
      } else {
        // Convert to integer
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) {
          transformed.record_order = numValue;
        } else {
          delete transformed.record_order; // Remove if not a valid number
        }
      }
    }
    
    // Ensure code is uppercase
    if (transformed.code) {
      transformed.code = transformed.code.toUpperCase();
    }
    
    return transformed;
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
